import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AuthenticationTokens } from "./AuthenticationTypes";
import { Principal, UserRole } from "./Principal";

export type Auth = {
	principal?: Principal | null;

	getBearerToken: () => Promise<string | null>;
	getRefreshToken: () => string | null;
	refreshTokens: (organisationId?: number) => Promise<void>;
	updateTokens: (tokens: AuthenticationTokens) => void;
	removeTokens: () => void;
};

export const useAuth = () => useContext(AuthContext);

export const usePrincipal = () => {
	const { principal } = useContext(AuthContext);
	return principal;
};

const voidFn = () => {
	// Nothing to be done
};

export const AuthContext = createContext<Auth>({
	principal: null,

	getBearerToken: () => Promise.resolve(null),
	getRefreshToken: () => null,
	refreshTokens: () => Promise.resolve(),
	updateTokens: voidFn,
	removeTokens: voidFn
});

type AuthProviderProps = {
	children?: React.ReactNode;
};

const AUTO_REFRESH_MARGIN = 5 * 60 * 1000;
const REQUEST_REFRESH_MARGIN = AUTO_REFRESH_MARGIN / 2;

let fetchTokenRequest: Promise<string> | null;

export function AuthProvider(props: Readonly<AuthProviderProps>) {
	const [accessToken, setAccessToken] = useState<string | null>();
	const [refreshToken, setRefreshToken] = useState<string | null>();
	const [principal, setPrincipal] = useState<Principal | null>();

	const context: Auth = useMemo(
		() => ({
			principal,

			getBearerToken,
			getRefreshToken,
			refreshTokens,

			updateTokens,
			removeTokens
		}),
		[accessToken, refreshToken, principal]
	);

	useEffect(() => {
		setAccessToken(localStorage.getItem("accessToken"));
		setRefreshToken(localStorage.getItem("refreshToken"));
	}, []);

	useEffect(() => {
		if (accessToken === undefined) {
			return voidFn;
		}

		if (accessToken === null) {
			setPrincipal(null);
			return voidFn;
		}

		const jwt = parseJwt(accessToken);
		const updatedPrincipal = parse(jwt);
		setPrincipal(updatedPrincipal);
		const remainingTime = jwt.exp * 1000 - Date.now() - AUTO_REFRESH_MARGIN;

		if (remainingTime < 0) {
			getTokens(
				updatedPrincipal.organisationId,
				updatedPrincipal.userEmail
			);
			return voidFn;
		} else {
			const id = setTimeout(() => {
				getTokens(
					updatedPrincipal.organisationId,
					updatedPrincipal.userEmail
				);
			}, remainingTime);
			return () => {
				clearTimeout(id);
			};
		}
	}, [accessToken]);

	async function getTokens(
		organisationId?: number,
		userEmail?: string
	): Promise<string> {
		const requestOptions = {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				email: userEmail ?? principal?.userEmail,
				refreshToken,
				organisationId
			})
		};

		fetchTokenRequest = fetch("/api/auth/refresh", requestOptions)
			.then(async response => {
				if (response.status === 401) {
					removeTokens();
				}

				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				const tokens = await response.json();
				updateTokens(tokens);
				return tokens.accessToken;
			})
			.finally(() => (fetchTokenRequest = null));

		return fetchTokenRequest;
	}

	function getBearerToken(): Promise<string | null> {
		if (!principal || !accessToken) {
			return Promise.resolve(null);
		}

		const remainingTime =
			principal.expiresAt * 1000 - Date.now() - REQUEST_REFRESH_MARGIN;
		if (remainingTime > 0) {
			return Promise.resolve(accessToken);
		}

		return fetchTokenRequest ?? getTokens(principal.organisationId);
	}

	// TODO: just a quick fix/hack for the logout!
	// TODO: logout should be refactored so it doesn't need the token.
	// TODO: instead we should work with sessions.
	function getRefreshToken(): string | null {
		if (!principal || !refreshToken) {
			return null;
		}

		return refreshToken;
	}

	function refreshTokens(organisationId?: number): Promise<void> {
		return getTokens(organisationId).then();
	}

	function updateTokens(tokens: AuthenticationTokens): void {
		localStorage.setItem("accessToken", tokens.accessToken);
		localStorage.setItem("refreshToken", tokens.refreshToken);

		setAccessToken(tokens.accessToken);
		setRefreshToken(tokens.refreshToken);
	}

	function removeTokens(): void {
		localStorage.removeItem("accessToken");
		localStorage.removeItem("refreshToken");

		setAccessToken(null);
		setRefreshToken(null);
	}

	function parse(token: Jwt): Principal {
		const userId = token.user_id;
		const userEmail = token.sub;
		const organisationId = token.organisation_id;
		const userRole = token.user_role;

		const expiry = token.exp;
		const signedEula = token.eula === "true" || token.eula === true;

		return new Principal(
			userId,
			userEmail,
			organisationId,
			userRole,
			expiry,
			signedEula
		);
	}

	return (
		<AuthContext.Provider value={context}>
			{props.children}
		</AuthContext.Provider>
	);
}

// TODO: where does this belong?
export type Jwt = {
	sub: string;
	eula: string | boolean; // TODO: only for backwards compatibility.
	user_id: number;
	organisation_id?: number;
	user_role?: UserRole;
	exp: number;
};

function parseJwt(token: string): Jwt {
	const base64Url = token.split(".")[1];
	const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split("")
			.map(function (c) {
				return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join("")
	);

	return JSON.parse(jsonPayload);
}
